import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Spinner from '../../../components/common/Spinner';
import {
  Flex,
  FlexCenter,
  FlexJustifyCenter
} from '../../../styles/stylesStyled';
import { AmountBox, Info, ShowButton, Subtitle, Title } from './WithdrawStyled';

import { useTranslation } from 'react-i18next';
import { Desktop } from '../../../App';
import { useAppSelector } from '../../../app/hooks';
import { CheckText } from '../../../components/common/CheckText';
import { formatUSD, showWallet, USDT } from '../../../helpers/helperFunctions';
import { P3 } from '../../../styles/FontStyles';
import { Button, CircleIconWrapper } from '../../../styles/GlobalComponents';
import { color } from '../../../styles/GlobalStyles';
import { Icon, IconWithdrawAlert } from '../../../styles/Icons';
import { useDispatch } from 'react-redux';
import { createWithdrawFlipme } from './Withdraw.actions';

interface WithdrawFinalCheckProps {
  setStep: Dispatch<SetStateAction<number>>;
  amount: string;
  selected: number;
  selectedWallet: any;
  selectedNetwork: any;
  selectedToken: any;
  setAmount: any;
  fee: number;
}

export const WithdrawFinalCheck = ({
  setStep,
  amount,
  selectedWallet,
  selectedNetwork,
  selectedToken,
  setAmount,
  fee
}: WithdrawFinalCheckProps) => {
  const { t } = useTranslation('earnings');
  const [checkbox, setCheckbox] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const [total, setTotal] = useState(0);
  const [loadingWithdraw, setLoadingWithdraw] = useState(false);

  const dispatch = useDispatch();

  const { loading } = useAppSelector((state) => state.withdraw);

  const { verificationStatus } = useAppSelector((state) => state.settings);

  const selectedNetworkType = (type: number) => {
    switch (type) {
      case 1:
        return 'BEP20.';

      case 2:
        return 'TRC20.';

      case 3:
        return 'ULC-20.';

      default:
        return '';
    }
  };

  useEffect(() => {
    setTotal(Number(amount) - Number(fee));
  }, [amount, total]);

  return (
    <div>
      <>
        {loading || loadingWithdraw ? (
          <Spinner />
        ) : (
          <>
            <Title>{t('withdraw_part.final_check.title')}</Title>
            <Subtitle>
              <AmountBox>
                <FlexJustifyCenter>
                  <CircleIconWrapper
                    color={color.colors.secondary100}
                    mb='10px'>
                    <Icon pointer w='34px' color={color.colors.neutral50}>
                      <IconWithdrawAlert />
                    </Icon>
                  </CircleIconWrapper>
                </FlexJustifyCenter>

                <P3 center bold color={color.colors.neutral500}>
                  WARNING
                </P3>

                <P3 center bold mt='10'>
                  PLEASE MAKE SURE THAT THE WALLET YOU ARE ADDING IS CAPABLE OF
                  RECEIVING {selectedToken?.token} ON{' '}
                  {selectedNetworkType(selectedNetwork?.networkType)}
                </P3>
              </AmountBox>
            </Subtitle>
            <AmountBox>
              <Info>
                <div>{t('withdraw_part.final_check.amount')}:</div>
                {amount} {selectedToken?.token}
              </Info>
              <Info style={{ wordBreak: 'break-all' }}>
                <div>{t('withdraw_part.final_check.network')}:</div>{' '}
                {selectedNetwork?.wallet}
              </Info>
              <Info style={{ wordBreak: 'break-all' }}>
                <div>{t('withdraw_part.final_check.address')}:</div>{' '}
                {showMore && selectedWallet.address}
                {!showMore && showWallet(selectedWallet.address, -9)}
                <Desktop>
                  {!showMore && (
                    <ShowButton onClick={() => setShowMore(true)}>
                      Show
                    </ShowButton>
                  )}
                </Desktop>
              </Info>
              <Info>
                <div>{t('withdraw_part.final_check.processing_fee')}:</div>{' '}
                {fee} {selectedToken?.token}
              </Info>
            </AmountBox>

            <Flex>
              <P3 bold mt='20' mb='20'>
                {t('withdraw_part.final_check.total')}:
              </P3>

              <P3 bold ml='10' mt='20' mb='20'>
                {formatUSD(total)} {selectedToken?.token}
              </P3>
            </Flex>

            <P3 bold color={color.colors.error500} mb='16'>
              {t('withdraw_part.final_check.info')}
            </P3>

            <CheckText
              checked={checkbox}
              onClick={() => setCheckbox(!checkbox)}
              text={t('withdraw_part.final_check.confirm_txt')}
            />

            <FlexCenter style={{ flexDirection: 'column' }}>
              <Button
                maxWidth='200px'
                m={'30px 0 0 0'}
                opacity={'true'}
                disabled={!checkbox}
                onClick={() => {
                  if (!verificationStatus) {
                    setStep(8);
                  } else {
                    if (selectedWallet.isFlipme) {
                      dispatch(
                        createWithdrawFlipme({
                          amount: parseInt(amount),
                          setStep,
                          networkType: selectedNetwork.networkType,
                          setAmount,
                          networkCurrencyType:
                            selectedToken.token.toLowerCase(),
                          setLoadingWithdraw
                        })
                      );
                    } else {
                      setStep(6);
                    }
                  }
                }}>
                {t('withdraw_part.final_check.withdraw')}
              </Button>
            </FlexCenter>
          </>
        )}
      </>
    </div>
  );
};
